import { DataGrid, useGridApiRef, gridClasses } from "@mui/x-data-grid";
import React, { forwardRef, useRef } from "react";
import { StyledContainer } from "./HyroTable.styles";
import useInlineEditing from "./hooks/InlineEditing.hook";
import usePagination from "./hooks/Pagination.hook";
import useTableData from "./hooks/TableData.hook";
import useSorting from "./hooks/Sorting.hook";
import useRowSelection from "./hooks/RowSelection.hook";
import { DEFAULT_EMPTY_TEXT, DEFAULT_EMPTY_SEARCH_DESCRIPTION, DEFAULT_EMPTY_SEARCH_TEXT, DEFAULT_ESTIMATED_ROW_HEIGHT, PAGINATION_MODES } from "./HyroTable.consts";
import NoRowsOverlay from "./components/NoRowsOverlay";
import TableFooter from "./components/TableFooter";

const HyroTable = forwardRef(({
  columns = [],
  rows = [],
  rowActions,
  onRowUpdate,
  showPagination = false,
  paginationMode = PAGINATION_MODES.CLIENT,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  testId,
  onAddInlineRow,
  showInlineAddRow = false,
  autoNavigateToLastPage = false,
  enableBulkEdit = false,
  selectedColumn,
  onRowSelectionModelChange,
  onRowClick,
  noRowsText = DEFAULT_EMPTY_TEXT,
  noResultsText = DEFAULT_EMPTY_SEARCH_TEXT,
  noResultsDescription = DEFAULT_EMPTY_SEARCH_DESCRIPTION,
  isFilteringOn = false,
  overrideMaxRowHeight = false,
  columnStyle,
  truncateCellText = true,
  disableColumnSorting,
  loading,
  sx,
  dataGridProps = {},
  stretchHeight = false,
  minHeight,
  disableHover = false,
  alwaysShowScroll = true,
  sortModel = [],
  onSortModelChange = () => {}
}, ref) => {
  const containerRef = useRef(null);
  const apiRef = useGridApiRef();
  const paginationData = usePagination({ showPagination, rows, autoNavigateToLastPage, apiRef, showInlineAddRow, paginationMode, paginationModel, onPaginationModelChange, rowCount });
  const sortingData = useSorting();
  const rowSelectionData = useRowSelection({ apiRef, enableBulkEdit, onRowSelectionModelChange }, ref);
  const inlineEditingData = useInlineEditing({ apiRef, columns });
  const tableData = useTableData({ columns, rows, rowActions, onRowUpdate, testId, columnStyle, overrideMaxRowHeight, truncateCellText, clearSelection: inlineEditingData.clearSelection, currentEditableField: inlineEditingData.currentEditableField, apiRef, selectedColumn });

  return (
    <StyledContainer
      data-testid={`${testId}-table`}
      ref={containerRef}
      stretchHeight={stretchHeight}
      stickyActions={tableData.isActionsSticky}
      minHeight={minHeight}
      disableHover={disableHover}
      alwaysShowScroll={alwaysShowScroll}
      clickable={onRowClick}
      enableBulkEdit={enableBulkEdit}
      hasSelectedRows={rowSelectionData.hasSelectedRows}
      selectedColumnIndex={tableData.selectedColumnIndex !== -1 ? tableData.selectedColumnIndex + 1 : undefined}>
      <DataGrid
        checkboxSelection={enableBulkEdit}
        apiRef={apiRef}
        rows={tableData.normalizedRows}
        columns={tableData.normalizedCols}
        onRowClick={onRowClick}
        disableColumnMenu
        disableVirtualization
        disableColumnResize
        disableRowSelectionOnClick
        columnHeaderHeight={48}
        getRowHeight={() => "auto"}
        disableColumnSorting={disableColumnSorting}
        getEstimatedRowHeight={() => DEFAULT_ESTIMATED_ROW_HEIGHT}
        loading={loading}
        localeText={{ columnHeaderSortIconLabel: "" }}
        slots={{
          noRowsOverlay: () => NoRowsOverlay({ isFilteringOn, hasRows: paginationData.displayedRows.length > 0, noRowsText, noResultsText, noResultsDescription }),
          footer: () => TableFooter({ showAddButton: paginationData.showAddButton, onAddInlineRow, testId }),
          ...sortingData.sortingSlots
        }}
        slotProps={{
          loadingOverlay: {
            variant: "skeleton",
            noRowsVariant: "skeleton"
          },
          baseCheckbox: rowSelectionData.baseCheckbox
        }}
        onRowSelectionModelChange={rowSelectionData.handleRowSelectionModelChange}
        processRowUpdate={tableData.handleRowUpdate}
        onProcessRowUpdateError={(error) => console.error(error)}
        cellModesModel={inlineEditingData.cellModesModel}
        onCellModesModelChange={inlineEditingData.handleCellModesModelChange}
        onCellClick={inlineEditingData.handleCellClick}
        onCellKeyDown={inlineEditingData.handleCellKeyDown}
        onCellEditStop={inlineEditingData.handleCellEditStop}
        sx={sx}
        paginationMode={paginationMode}
        sortModel={paginationMode === PAGINATION_MODES.SERVER ? sortModel : undefined}
        onSortModelChange={paginationMode === PAGINATION_MODES.SERVER ? onSortModelChange : undefined}
        sortingOrder={sortingData.sortingOrder}
        paginationModel={paginationData.paginationModel}
        onPaginationModelChange={paginationData.setPaginationModel}
        onRowCountChange={paginationData.onRowCountChange}
        hideFooter={!showPagination}
        hideFooterPagination={!showPagination}
        pageSizeOptions={paginationData.pageSizeOptions}
        initialState={{ pagination: paginationData.paginationInitialState }}
        rowCount={paginationMode === PAGINATION_MODES.SERVER ? paginationData.rowCount : undefined}
        {...dataGridProps}
      />
    </StyledContainer>
  );
});

HyroTable.displayName = "HyroTable";
export default HyroTable;
